import { m } from "@vega-core/paraglide/messages";

import { convertToTitleCase } from "./formatString";

/**
 * Converts an array of strings to a single string.
 *
 * Each string in the array is transformed from snake case to human-readable format.
 * The transformed strings are then joined together with a comma and a space.
 *
 * If the input array is undefined or empty, an empty string is returned.
 *
 * @param {string[]} array - The array of strings to convert.
 * @param {boolean} [sortAlpha=false] - Whether to sort the array alphabetically before converting.
 * @returns {string} The resulting comma-separated string of human-readable names.
 */
export const arrayToString = (array?: string[], sortAlpha = false): string => {
  if (!array || array.length === 0) return "";

  const processedArray = sortAlpha
    ? array.toSorted((a, b) => a.localeCompare(b))
    : array;

  return processedArray.map(convertToTitleCase).join(", ");
};

export const useArrayToString = () => {
  /**
   * Converts an array of strings to a single string.
   *
   * Each string in the array is transformed from snake case to human-readable format.
   * The transformed strings are then joined together with a comma and a space.
   * If the array has more than one element, the last element is joined with an "and".
   *
   * If the input array is undefined or empty, an empty string is returned.
   *
   * @param {string[]} array - The array of strings to convert.
   * @param {boolean} [sortAlpha=false] - Whether to sort the array alphabetically before converting.
   * @returns {string} The resulting comma-separated string of human-readable names with an "and" separator.
   */
  const arrayToStringWithAnd = (
    array?: string[],
    sortAlpha = false,
  ): string => {
    if (!array || array.length === 0) return "-";

    const processedArray = sortAlpha
      ? array.toSorted((a, b) => a.localeCompare(b))
      : array;

    if (processedArray.length === 1) return processedArray[0];

    return `${processedArray.slice(0, -1).join(", ")} ${m.conjunctions_and()} ${processedArray[processedArray.length - 1]}`;
  };

  return { arrayToStringWithAnd };
};
